// src/components/forest/ActionMenu.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GAME_CONFIG } from '../../utils/gameConfig';
import { GameAction, Tree } from '../../types';

interface ActionMenuProps {
  x: number; // tile coordinates
  y: number; // tile coordinates
  actions: GameAction[];
  onAction: (action: GameAction) => void;
  onClose: () => void;
  isVisible: boolean;
  trees?: Tree[]; // Optional: for showing tree context
  userDirection?: 'up' | 'down' | 'left' | 'right'; // Optional: for showing which tree is targeted
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  x,
  y,
  actions,
  onAction,
  onClose,
  isVisible,
  trees = [],
  userDirection = 'down'
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset selection when menu opens
  useEffect(() => {
    if (isVisible) {
      setSelectedIndex(0);
    }
  }, [isVisible]);

  // Keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      console.log('🎯 ActionMenu key pressed:', e.key);
      
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(actions.length - 1, prev + 1));
          break;
        case 'Enter':
          // REMOVED spacebar case to prevent conflict!
          e.preventDefault();
          console.log('🎯 ActionMenu executing action via Enter:', actions[selectedIndex]);
          onAction(actions[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, actions, selectedIndex, onAction, onClose]);

  // Convert action to display text
  const getActionText = (action: GameAction): string => {
    switch (action.type) {
      case 'plant_seed':
        return '🌱 Plant seed here';
      case 'water_tree':
        // Find the tree being watered for more context
        const tree = trees.find(t => t.id === action.treeId);
        if (tree) {
          const directionEmoji = userDirection === 'up' ? '⬆️' : 
                                 userDirection === 'down' ? '⬇️' : 
                                 userDirection === 'left' ? '⬅️' : '➡️';
          return `💧 Water ${tree.planterName}'s tree ${directionEmoji}`;
        }
        return '💧 Water tree';
      case 'exit_menu':
        return '❌ Exit';
      default:
        return 'Unknown action';
    }
  };

  // Calculate pixel position for menu - MUCH simpler
  const pixelX = x * GAME_CONFIG.TILE_SIZE;
  const pixelY = y * GAME_CONFIG.TILE_SIZE;

  console.log('🎯 ActionMenu positioning debug:', {
    isVisible,
    avatarTilePos: { x, y },
    avatarPixelInGrid: { pixelX, pixelY },
    menuPosition: {
      left: pixelX + GAME_CONFIG.TILE_SIZE + 8,
      top: pixelY - 8
    },
    actionsCount: actions.length
  });

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="absolute z-50 bg-white border-2 border-gray-800 rounded-lg shadow-2xl min-w-48"
          style={{
            left: pixelX + GAME_CONFIG.TILE_SIZE + 8, // Simple: just to the right of the avatar
            top: pixelY - 8, // Simple: slightly above the avatar
          }}
        >
          <div className="p-3">
            <div className="text-xs text-gray-600 mb-2 text-center font-medium">
              Use ↑↓ arrows, Enter to select
            </div>
            {actions.map((action, index) => (
              <motion.div
                key={`${action.type}-${index}`}
                className={`
                  px-3 py-2 rounded cursor-pointer transition-colors text-sm font-medium
                  ${index === selectedIndex 
                    ? 'bg-blue-500 text-white shadow-md' 
                    : 'hover:bg-blue-100 text-gray-800 bg-gray-50'
                  }
                `}
                onClick={() => onAction(action)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {getActionText(action)}
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ActionMenu;